import React from "react";
import ServicesView from "./ServicesView";
import { useServiceCatalog } from "@/hooks/services/useServiceCatalog";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";
import { useStepCta } from "../useStepCta";
import { formatDurationSeconds } from "@/lib/utils";
import { useBookingDraft } from "../BookingDraftContext";
import { getServiceRegistry } from "@/lib/services/serviceRegistrySingleton";
import { useTranslation } from "react-i18next";

/**
 * Adapter container that maps registry snapshot -> presentational props expected by ServicesView.
 * The widget bootstrap must ensure API scope and session are ready so we enable the query.
 */
export default function ServicesViewContainer({
  onBack,
  onContinue,
  onPickAnotherMaster,
  onGoToSpecialist,
  onGoToServices,
  onOpenClientDashboard,
  onOpenSupport,
  preselectedServiceId,
  preselectedCategoryId,
  onAutoAdvance,
}: {
  onBack: () => void;
  onContinue?: () => void;
  onPickAnotherMaster?: () => void;
  onGoToSpecialist: () => void;
  onGoToServices: () => void;
  onOpenClientDashboard?: () => void;
  onOpenSupport?: () => void;
  preselectedServiceId?: string | null;
  preselectedCategoryId?: string | null;
  onAutoAdvance?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { data: snap, isLoading, error } = useServiceCatalog({ enabled: true });
  const currency = getCompanyRegistry().getSettings()?.currency_short_title ?? "BYN";
  const numberLocale = i18n.resolvedLanguage || i18n.language || "en";

  const { draft, actions } = useBookingDraft();
  const masterId = draft.specialistId && draft.specialistId !== "any" ? Number(draft.specialistId) : null;

  const categories = React.useMemo(() => {
    if (!snap) return [];
    const reg = getServiceRegistry();

    let cats = snap.categoryOrder.map((cid: number) => {
      const c = snap.categories[cid];
      if (!c) return null;

      // base ids in this category
      let ids: number[] = c.serviceIds ?? [];

      // filter by master if chosen (use snapshot forward mapping for reliability)
      if (masterId) {
        ids = ids.filter((sid) => {
          const s = snap.services[sid];
          // staffIds is filled during registry merge from service.staff
          const staffIds: number[] = Array.isArray(s?.staffIds) ? s.staffIds : [];
          return staffIds.includes(masterId);
        });
      }

      const services = ids
        .map((sid: number) => snap.services[sid])
        .filter(Boolean)
        .map((s: any) => {
          // Prefer master-specific duration if present (>0); otherwise fall back to service default
          const custom = masterId ? Number(s?.staffDurationSeconds?.[masterId]) : NaN;
          const durSec =
            Number.isFinite(custom) && custom > 0 ? custom : Number(s?.duration_seconds ?? 0);
          const priceMin = Number(s?.price_min ?? 0);
          const priceMax = Number(s?.price_max ?? 0);
          const hasRange =
            Number.isFinite(priceMin) &&
            Number.isFinite(priceMax) &&
            priceMin > 0 &&
            priceMax > 0 &&
            priceMin !== priceMax;
          const priceLabel = hasRange
            ? `${new Intl.NumberFormat(numberLocale).format(priceMin)} - ${new Intl.NumberFormat(numberLocale).format(priceMax)}`
            : new Intl.NumberFormat(numberLocale).format(priceMin);

          return {
            id: String(s.id),
            name: s.title,
            duration: formatDurationSeconds(durSec),
            description: s.comment || "",
            priceMin,
            priceMax,
            priceLabel,
            currency,
            lastHour: s.last_hour
              ? (() => {
                  const pct = Number(s.last_hour.discount_pct);
                  const fmt = (v: number) =>
                    new Intl.NumberFormat(numberLocale).format(Math.round(v * 100) / 100);
                  const dMin = priceMin * (1 - pct / 100);
                  const dMax = priceMax * (1 - pct / 100);
                  return {
                    discountPct: pct,
                    price: Number(s.last_hour.price ?? 0),
                    regularPrice: Number(s.last_hour.regular_price ?? 0),
                    // ranged services get a discounted range — the final price
                    // is settled by the admin at completion, as usual
                    discountedLabel: hasRange ? `${fmt(dMin)} - ${fmt(dMax)}` : fmt(dMax),
                  };
                })()
              : undefined,
          };
        });
      return { id: String(c.id), name: c.name, services };
    }).filter(Boolean) as { id: string; name: string; services: any[] }[];

    // NEW: when a master is chosen, show ONLY categories that master actually serves
    if (masterId) {
      cats = cats.filter((c) => (c.services?.length ?? 0) > 0);
    }
    return cats;
  }, [snap, currency, masterId, numberLocale]);

  // Local UI state copied from BookingFlow default shape (keeps ServicesView API)
  // Start empty — we'll seed it to the first *filled* category for the chosen master
  // Seed with preselected category if provided; the existing effect will realign if invalid
  const [selectedCategory, setSelectedCategory] = React.useState<string>(preselectedCategoryId || "");
  const [searchQuery, setSearchQuery] = React.useState<string>("");
  const cta = useStepCta(draft, "services", t);
  const ctaLabel = cta?.label ?? t("widget.datetime.choose", "Choose date & time");

  // first category that actually contains services (after master filtering)
  const firstFilledCategoryId = React.useMemo(
    () => categories.find((c) => (c.services?.length ?? 0) > 0)?.id ?? categories[0]?.id ?? "",
    [categories]
  );

  // Seed/realign the tab to the first *filled* category when needed.
  // Triggers when categories change or the selected master changes.
  React.useEffect(() => {
    if (!categories.length) return;

    const current = categories.find((c) => c.id === selectedCategory);
    const currentIsEmpty = !current || (current.services?.length ?? 0) === 0;

    // If nothing selected yet, or the current tab is empty for this master, jump to the first filled one.
    if (!selectedCategory && firstFilledCategoryId) {
      setSelectedCategory(firstFilledCategoryId);
      return;
    }

    if (currentIsEmpty && firstFilledCategoryId && selectedCategory !== firstFilledCategoryId) {
      setSelectedCategory(firstFilledCategoryId);
    }
  }, [categories, selectedCategory, firstFilledCategoryId, masterId]);

  // ── Service preselection handling ─────────────────────────────────────
  // IMPORTANT: these hooks MUST be above the early returns to satisfy Rules of Hooks.
  const [servicePreselectionHandled, setServicePreselectionHandled] = React.useState(false);
  const [serviceNotFound, setServiceNotFound] = React.useState(false);
  const [serviceFoundName, setServiceFoundName] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (servicePreselectionHandled || !preselectedServiceId || isLoading || !snap) return;

    // Look up the service in the flat snapshot map (keyed by numeric ID)
    const found = snap.services[Number(preselectedServiceId)];

    setServicePreselectionHandled(true);

    if (found) {
      // Service exists → select it in the draft and show success overlay
      actions.toggleService(preselectedServiceId);

      // Also jump the category tab to the one containing this service
      const ownerCat = categories.find((c) =>
        c.services.some((s: any) => s.id === preselectedServiceId),
      );
      if (ownerCat) setSelectedCategory(ownerCat.id);

      setServiceFoundName(found.title || found.name || preselectedServiceId);
    } else {
      // Service not found → show overlay
      setServiceNotFound(true);
    }
  }, [servicePreselectionHandled, preselectedServiceId, isLoading, snap, categories]);
  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) return <div className="p-6 text-center text-muted-foreground">{t("widget.services.loading", "Loading services...")}</div>;
  if (error) return <div className="p-6 text-center text-muted-foreground">{t("widget.services.error", "Could not load services. Please try again later.")}</div>;

  const currentCategory = categories.find((c) => c.id === selectedCategory);
  const categoryEmptyForMaster = !!masterId && (!!currentCategory && currentCategory.services.length === 0);

  return (
    <div className="relative h-full">
      {/* Services list — faded when any overlay is active */}
      <div className={(serviceNotFound || serviceFoundName) ? "opacity-25 pointer-events-none select-none" : undefined}>
        <ServicesView
          categories={categories}
          selectedCategory={selectedCategory}
          selectedServices={draft.serviceIds}
          searchQuery={searchQuery}
          onBack={onBack}
          onSelectCategory={setSelectedCategory}
          onToggleService={(id: string) => actions.toggleService(id)}
          onSearch={setSearchQuery}
          onContinue={onContinue}
          ctaLabel={ctaLabel}
          // NEW:
          categoryEmptyForMaster={categoryEmptyForMaster}
          onPickAnotherMaster={onPickAnotherMaster}
          onGoToSpecialist={onGoToSpecialist}
          onGoToServices={onGoToServices}
          onOpenClientDashboard={onOpenClientDashboard}
          onOpenSupport={onOpenSupport}
          initialScrollCategoryId={preselectedCategoryId}
        />
      </div>

      {/* "Service selected" success overlay */}
      {serviceFoundName && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/60">
          <div className="mx-6 max-w-sm rounded-2xl bg-card p-8 text-center shadow-xl border border-border">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">
              {t("widget.services.preselected_success_title", "Service selected")}
            </h3>
            <p className="mb-6 text-sm text-muted-foreground">
              {t(
                "widget.services.preselected_success_body",
                "\"{{name}}\" has been added to your booking.",
                { name: serviceFoundName },
              )}
            </p>
            <button
              className="w-full rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              onClick={() => {
                setServiceFoundName(null);
                onAutoAdvance?.();
              }}
            >
              {t("widget.common.ok", "OK")}
            </button>
          </div>
        </div>
      )}

      {/* "Service not found" error overlay */}
      {serviceNotFound && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-background/60">
          <div className="mx-6 max-w-sm rounded-2xl bg-card p-8 text-center shadow-xl border border-border">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-destructive" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">
              {t("widget.services.preselected_not_found_title", "Service not found")}
            </h3>
            <p className="mb-6 text-sm text-muted-foreground">
              {t(
                "widget.services.preselected_not_found_body",
                "The requested service is not available. Please choose from the available services below.",
              )}
            </p>
            <button
              className="w-full rounded-xl bg-primary px-5 py-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              onClick={() => setServiceNotFound(false)}
            >
              {t("widget.services.select_available", "Select from available services")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

